# Route: /admin/tenants/:tenantId
# Tenant detail: info, config, invitations, audit link

page-admin-tenant-detail:
  type: Page
  archetype: detail
  meta:
    title: $t.admin.tenants.detail_title
    auth: required
  params:
    tenantId:
      type: string
      required: true
  data:
    tenant:
      model: TenantResponse
      key: $route.tenantId
      refresh: on_mount
    # The profile is an optional part of this page: a failed profile read must not hide the
    # tenant that did load. `required: false` fetches it on its own, leaves it empty on failure
    # and records the failure under `_dataErrors.profile`, which the card reads below. The
    # viewed tenant's profile is the `of_tenant` read of the same model the caller's own
    # profile page reads.
    profile:
      model: TenantProfileResponse
      read: of_tenant
      key: $route.tenantId
      refresh: on_mount
      required: false
  spatial:
    display: flex
    direction: column
    gap: $spacing.lg
    padding: $spacing.lg
    padding_inline: $spacing.xl
    overflow: auto
    responsive:
      sm:
        padding: $spacing.sm
        padding_inline: $spacing.sm
        gap: $spacing.md
  tokens:
    background: $color_scheme.light.color.surface
  children:
    - tenant-detail-header
    - tenant-detail-info
    - tenant-detail-profile

# ── Header ───────────────────────────────────────────────────────────────────

tenant-detail-header:
  type: PageHeader
  back_route: /admin/tenants
  title_prefix: $t.admin.tenants.detail_title_prefix
  bind:
    content: tenant.name
  actions:
    # First, so the auto-applied `variant: filled` lands on an action that acts on the tenant on
    # screen. It carries no `permission:`, so the primary is never a hidden button.
    - label: $t.admin.tenants.manage_invitations
      node_id: tenant-invitations-btn
      a11y:
        label: $t.admin.tenants.manage_invitations_label
      route: /admin/tenants/:tenantId/invitations
      color: info
      # variant: filled — auto-applied as the first primary-eligible action
    # Gated on System:admin, the catalog permission nav-platform-tenants itself uses.
    # "Tenant:config-write" is a narrower permission the SA JWT's client-side set does
    # not carry, so gating on it hid the button from every system admin.
    - label: $t.admin.tenants.configure_features
      node_id: tenant-features-btn
      a11y:
        label: $t.admin.tenants.configure_features_label
      route: /admin/tenants/:tenantId/features
      # Captures this detail page as the return origin so Save/Cancel on the
      # features screen land back here, not on the tenant list (the
      # features Form's on_success cannot interpolate :tenantId, so the
      # return channel is what carries the id back).
      return_context: origin_only
      color: info
      permission: "System:admin"
    # Canonical /audit deep-link (screen-guide.md §6c): the forward channel
    # (query:) pre-applies the Tenant filter on load; the return channel
    # (return_context: origin_only) gives /audit a contextual Back to this
    # detail page. Read-only navigation, so tertiary.
    - label: $t.admin.tenants.view_audit
      a11y:
        label: $t.admin.tenants.view_audit_label
      route: /audit
      query:
        entityType: Tenant
        entityId: $route.tenantId
      return_context: origin_only
      color: info
      variant: ghost
  overflow:
    # NO CLIENT-SIDE `permission:` GATE, deliberately. The server enforces
    # Tenant:suspend and Tenant:activate (TenantEndpoints, .RequirePermission("Tenant", ...)),
    # but no starter role holds either verb — both are on
    # GateSatisfiabilitySweep.IntentionalAdminOnlyGates — and a verified SystemAdmin reaches the
    # routes by BYPASSING the tenant-scoped check (RequirePermissionFilter, ISystemAdminGate), not
    # by holding them. So the verbs are absent from the SA's client-side permission set, and
    # gating on them would hide these items from the only people who use them. `System:admin`
    # would work, but the page each item opens carries
    # auth: required and the server refuses the act itself, so a client gate duplicating the
    # nav gate that already admitted the reader buys nothing it can enforce.
    #
    # Suspend and activate are both REQUIRED-reason transactions (dsl/defaults/tenant.policy.yaml),
    # so neither act can be a bare call from this menu: a body-less PUT is refused 422
    # XP87-REASON-REQUIRED. Each item opens the page that captures the reason, and that page
    # performs the transition.
    #
    # Each item's visibility binds to a server-computed flag (tenant.flags.can*), never to
    # `status`: the flags are read off the Tenant aggregate (models.cir.yaml
    # TenantTransitionFlags), so a state added to TenantStatus or a widened transition changes
    # the offer with no CIR edit. canSuspend is the predicate Suspend itself guards on.
    # canActivate is narrower than what Activate guards on: it is the operator-admissible
    # subset (CanReactivate — from Suspended only), false on a Provisioning tenant although
    # the aggregate admits that activation, because it is the provisioning process's own
    # completion, never an operator's act. The server still refuses an inadmissible
    # transition; a flag never offers one it would refuse.
    - label: $t.admin.tenants.suspend
      a11y:
        label: $t.admin.tenants.suspend_label
      route: /admin/tenants/:tenantId/suspend
      color: stop
      visible_when: tenant.flags.canSuspend
    - label: $t.admin.tenants.activate
      a11y:
        label: $t.admin.tenants.activate_label
      route: /admin/tenants/:tenantId/activate
      color: go
      visible_when: tenant.flags.canActivate

# ── Info card ────────────────────────────────────────────────────────────────

tenant-detail-info:
  type: Section
  spatial:
    display: flex
    direction: column
    gap: $spacing.md
    padding: $spacing.lg
  tokens:
    background: $color_scheme.light.color.surface-elevated
    radius: $radius.lg
    shadow: $color_scheme.light.shadow.sm
  children:
    - tenant-info-name
    - tenant-info-id
    - tenant-info-status
    - tenant-info-created

tenant-info-name:
  type: Section
  spatial:
    display: flex
    gap: $spacing.sm
  children:
    - tenant-info-name-label
    - tenant-info-name-value

tenant-info-name-label:
  type: Text
  content: $t.admin.tenants.label_name
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

tenant-info-name-value:
  type: Text
  bind:
    content: tenant.name
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface

tenant-info-id:
  type: Section
  spatial:
    display: flex
    gap: $spacing.sm
  children:
    - tenant-info-id-label
    - tenant-info-id-value

tenant-info-id-label:
  type: Text
  content: $t.admin.tenants.label_id
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

tenant-info-id-value:
  type: Text
  bind:
    content: tenant.id
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface

tenant-info-status:
  type: Section
  spatial:
    display: flex
    gap: $spacing.sm
  children:
    - tenant-info-status-label
    - tenant-info-status-value

tenant-info-status-label:
  type: Text
  content: $t.admin.tenants.label_status
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

tenant-info-status-value:
  type: Badge
  bind:
    content: tenant.status

tenant-info-created:
  type: Section
  spatial:
    display: flex
    gap: $spacing.sm
  children:
    - tenant-info-created-label
    - tenant-info-created-value

tenant-info-created-label:
  type: Text
  content: $t.admin.tenants.label_created
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

tenant-info-created-value:
  type: Text
  bind:
    content: tenant.createdAt
  format: datetime
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface

# ── Organization Profile (F46) ───────────────────────────────────────────────
#
# An UNSET profile is a server default, not a missing row: GetTenantProfileQuery.CreateDefault
# answers displayName = the tenant's name, siteCode null, technicalContact null, and the
# primary contact's email and every address field as EMPTY STRINGS. A stored profile can never
# carry those blanks — ContactInfo refuses a blank email and Address a blank city — so the
# rows below read two facts off the payload and need no flag of their own:
#
#   - each optional row is guarded on its own value (`visible_when: <path>`, a truthy test),
#     so an absent value hides its row instead of leaving a bare label;
#   - "no profile recorded" is exactly `profile.primaryContact.email == ''`. The comparison is
#     to the empty STRING, never a negation (`!profile.primaryContact.email`): before the fetch
#     resolves the path is undefined, a negation is true, and the line would flash on every
#     tenant while the page loads; `undefined == ''` is false.
#
# One comparison per predicate, deliberately: the visible_when grammar takes a single path and
# an optional single comparison (VisibleWhenEmitter.ToJs), with no `&&` / `||`.

tenant-detail-profile:
  type: Section
  spatial:
    display: flex
    direction: column
    gap: $spacing.md
    padding: $spacing.lg
  tokens:
    background: $color_scheme.light.color.surface-elevated
    radius: $radius.lg
    shadow: $color_scheme.light.shadow.sm
  children:
    - tenant-profile-title
    - tenant-profile-error
    - tenant-profile-display-name
    - tenant-profile-empty
    - tenant-profile-site-code
    - tenant-profile-primary-contact
    - tenant-profile-technical-contact
    - tenant-profile-address

tenant-profile-title:
  type: Text
  content: $t.admin.tenants.profile_title
  a11y:
    role: heading
    level: 2
  tokens:
    font_size: $font.size.lg
    color: $color_scheme.light.color.on-surface

tenant-profile-error:
  type: Text
  content: $t.admin.tenants.profile_unavailable
  visible_when: _dataErrors.profile
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

tenant-profile-display-name:
  type: Section
  visible_when: profile.displayName
  spatial:
    display: flex
    gap: $spacing.sm
  children:
    - tenant-profile-display-name-label
    - tenant-profile-display-name-value

tenant-profile-display-name-label:
  type: Text
  content: $t.admin.tenants.profile_display_name
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

tenant-profile-display-name-value:
  type: Text
  bind:
    content: profile.displayName
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface

# The muted single-line note shape of audit-detail's `audit-info-source-text-id-note`. Not an
# EmptyState: that node is a collection's empty region (a DataTable's `empty:`), and this
# card is not a collection — Display Name still renders above it.
tenant-profile-empty:
  type: Text
  content: $t.admin.tenants.profile_empty
  visible_when: profile.primaryContact.email == ''
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

tenant-profile-site-code:
  type: Section
  visible_when: profile.siteCode
  spatial:
    display: flex
    gap: $spacing.sm
  children:
    - tenant-profile-site-code-label
    - tenant-profile-site-code-value

tenant-profile-site-code-label:
  type: Text
  content: $t.admin.tenants.profile_site_code
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

tenant-profile-site-code-value:
  type: Text
  bind:
    content: profile.siteCode
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface

tenant-profile-primary-contact:
  type: Section
  visible_when: profile.primaryContact.email
  spatial:
    display: flex
    gap: $spacing.sm
  children:
    - tenant-profile-primary-contact-label
    - tenant-profile-primary-contact-value

tenant-profile-primary-contact-label:
  type: Text
  content: $t.admin.tenants.profile_primary_contact
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

tenant-profile-primary-contact-value:
  type: Text
  bind:
    content: profile.primaryContact.email
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface

tenant-profile-technical-contact:
  type: Section
  visible_when: profile.technicalContact.email
  spatial:
    display: flex
    gap: $spacing.sm
  children:
    - tenant-profile-technical-contact-label
    - tenant-profile-technical-contact-value

tenant-profile-technical-contact-label:
  type: Text
  content: $t.admin.tenants.profile_technical_contact
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

tenant-profile-technical-contact-value:
  type: Text
  bind:
    content: profile.technicalContact.email
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface

tenant-profile-address:
  type: Section
  visible_when: profile.address.city
  spatial:
    display: flex
    gap: $spacing.sm
  children:
    - tenant-profile-address-label
    - tenant-profile-address-value

tenant-profile-address-label:
  type: Text
  content: $t.admin.tenants.profile_address
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

tenant-profile-address-value:
  type: Text
  bind:
    content: profile.address.city
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface
